import { useCallback, useState, useMemo, createContext, useContext } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  ControlButton,
  MiniMap,
  Handle,
  Position,
  BaseEdge,
  getSmoothStepPath,
  useNodesState,
  useEdgesState,
  useInternalNode,
  useReactFlow,
  useStore,
  type NodeProps,
  type EdgeProps,
  type Node,
  type Edge,
  type InternalNode,
} from "@xyflow/react";
import { ConnectionMode } from "@xyflow/system";
import "@xyflow/react/dist/style.css";

// Generic, page-agnostic schema diagram. Pages pass an array of TableSpec
// (with positions + columns) and JoinSpec (with kind + label); this component
// renders a draggable ReactFlow canvas with click-to-collapse table nodes,
// a search input, and a colour-coded legend. Used inside QueryInspector's
// Schema tab.

export type Schema = "public" | "reporting" | "cte";

export type TableSpec = {
  id: string;
  label: string;
  schema: Schema;
  alias: string;
  x: number;
  y: number;
  columns: { name: string; note?: string }[];
};

export type JoinKind = "direct" | "cte" | "email";

export type JoinSpec = {
  id: string;
  source: string;
  target: string;
  label?: string;
  kind: JoinKind;
};

type TableNodeData = {
  label: string;
  schema: Schema;
  alias: string;
  columns: { name: string; note?: string }[];
  collapsed: boolean;
};

type TableNode = Node<TableNodeData, "table">;

const SCHEMA_COLORS: Record<string, string> = {
  public: "var(--color-teal)",
  reporting: "var(--color-coral)",
  cte: "#5F6FA0",
};

const KIND_COLOR: Record<JoinKind, string> = {
  direct: "var(--color-teal)",
  cte: "#5F6FA0",
  email: "var(--color-coral)",
};

const KIND_DASH: Record<JoinKind, string | undefined> = {
  direct: undefined,
  cte: "6 3",
  email: "5 4",
};

const HANDLE_STYLE: React.CSSProperties = {
  width: 1, height: 1, background: "transparent", border: "none", opacity: 0, pointerEvents: "none",
};

// ─── Floating edge: dynamically routes to whichever side is closest ──────────

type Side = "top" | "right" | "bottom" | "left";

function nodeBox(node: InternalNode) {
  const w = node.measured?.width ?? 200;
  const h = node.measured?.height ?? 36;
  const ax = node.internals.positionAbsolute.x;
  const ay = node.internals.positionAbsolute.y;
  return { ax, ay, w, h, cx: ax + w / 2, cy: ay + h / 2 };
}

function sideFromDelta(dx: number, dy: number): Side {
  if (Math.abs(dx) >= Math.abs(dy)) return dx > 0 ? "right" : "left";
  return dy > 0 ? "bottom" : "top";
}

function sideEndpoint(
  box: ReturnType<typeof nodeBox>,
  side: Side,
  slotIndex: number,
  slotCount: number,
): { x: number; y: number; pos: Position } {
  const MARGIN = 0.2;
  const frac = slotCount <= 1 ? 0.5 : MARGIN + (slotIndex / (slotCount - 1)) * (1 - 2 * MARGIN);
  switch (side) {
    case "top":    return { x: box.ax + box.w * frac, y: box.ay,           pos: Position.Top };
    case "bottom": return { x: box.ax + box.w * frac, y: box.ay + box.h,   pos: Position.Bottom };
    case "left":   return { x: box.ax,                y: box.ay + box.h * frac, pos: Position.Left };
    case "right":  return { x: box.ax + box.w,        y: box.ay + box.h * frac, pos: Position.Right };
  }
}

type FloatingEdgeData = { label?: string; edgeStyle?: React.CSSProperties; labelColor?: string };

function FloatingEdge({ id, source, target, data, markerEnd }: EdgeProps & { data?: FloatingEdgeData }) {
  const src = useInternalNode(source);
  const tgt = useInternalNode(target);
  const allEdges = useStore((s) => s.edges);
  const nodeLookup = useStore((s) => s.nodeLookup);
  if (!src || !tgt) return null;
  const sBox = nodeBox(src);
  const tBox = nodeBox(tgt);
  const srcSide = sideFromDelta(tBox.cx - sBox.cx, tBox.cy - sBox.cy);
  const tgtSide = sideFromDelta(sBox.cx - tBox.cx, sBox.cy - tBox.cy);
  const slotFor = (nodeId: string, side: Side): { index: number; count: number } => {
    const siblings: string[] = [];
    for (const e of allEdges) {
      const n1 = nodeLookup.get(e.source);
      const n2 = nodeLookup.get(e.target);
      if (!n1 || !n2) continue;
      const b1 = nodeBox(n1);
      const b2 = nodeBox(n2);
      const eSrcSide = sideFromDelta(b2.cx - b1.cx, b2.cy - b1.cy);
      const eTgtSide = sideFromDelta(b1.cx - b2.cx, b1.cy - b2.cy);
      if (e.source === nodeId && eSrcSide === side) siblings.push(e.id);
      else if (e.target === nodeId && eTgtSide === side) siblings.push(e.id);
    }
    siblings.sort();
    return { index: Math.max(0, siblings.indexOf(id)), count: siblings.length };
  };
  const srcSlot = slotFor(source, srcSide);
  const tgtSlot = slotFor(target, tgtSide);
  const sp = sideEndpoint(sBox, srcSide, srcSlot.index, srcSlot.count);
  const tp = sideEndpoint(tBox, tgtSide, tgtSlot.index, tgtSlot.count);
  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX: sp.x, sourceY: sp.y, sourcePosition: sp.pos,
    targetX: tp.x, targetY: tp.y, targetPosition: tp.pos,
    borderRadius: 12,
  });
  return (
    <>
      <BaseEdge id={id} path={path} style={data?.edgeStyle} markerEnd={markerEnd} />
      {data?.label && (
        <text
          x={labelX} y={labelY} textAnchor="middle" dominantBaseline="middle"
          stroke="var(--bg-1)" strokeWidth={4} strokeLinejoin="round"
          style={{ fontSize: 10, fill: data.labelColor ?? "var(--fg-3)", paintOrder: "stroke fill", pointerEvents: "none" }}
        >
          {data.label}
        </text>
      )}
    </>
  );
}

// ─── Search highlighting ─────────────────────────────────────────────────────

const SearchContext = createContext<string>("");

function matchesSearch(data: TableNodeData, q: string): { table: boolean; cols: Set<string> } {
  const cols = new Set<string>();
  if (!q) return { table: false, cols };
  const needle = q.toLowerCase();
  const table = data.label.toLowerCase().includes(needle) || data.alias.toLowerCase().includes(needle);
  for (const c of data.columns) {
    if (c.name.toLowerCase().includes(needle)) cols.add(c.name);
  }
  return { table, cols };
}

// ─── Table node component ────────────────────────────────────────────────────

function TableNodeComponent({ data }: NodeProps<TableNode>) {
  const accent = SCHEMA_COLORS[data.schema] ?? "var(--fg-3)";
  const search = useContext(SearchContext);
  const match = matchesSearch(data, search);
  const searchActive = search.length > 0;
  const nodeMatches = match.table || match.cols.size > 0;
  const dimmed = searchActive && !nodeMatches;
  const emphasized = searchActive && nodeMatches;
  return (
    <div style={{
      background: "var(--bg-1)",
      border: `${emphasized ? 2.5 : 1.5}px solid ${emphasized ? "var(--color-coral)" : accent}`,
      borderRadius: 8, minWidth: 190, fontSize: 12,
      boxShadow: emphasized ? "0 0 0 3px rgba(220,106,76,0.25), 0 2px 8px rgba(0,0,0,0.10)" : "0 2px 8px rgba(0,0,0,0.10)",
      opacity: dimmed ? 0.25 : 1,
      transition: "opacity 0.15s, box-shadow 0.15s, border-color 0.15s",
      cursor: "pointer", userSelect: "none",
    }}>
      <Handle type="source" position={Position.Top}    style={HANDLE_STYLE} />
      <Handle type="source" position={Position.Right}  style={HANDLE_STYLE} />
      <Handle type="source" position={Position.Bottom} style={HANDLE_STYLE} />
      <Handle type="source" position={Position.Left}   style={HANDLE_STYLE} />
      <Handle type="target" position={Position.Top}    id="tt" style={HANDLE_STYLE} />
      <Handle type="target" position={Position.Right}  id="tr" style={HANDLE_STYLE} />
      <Handle type="target" position={Position.Bottom} id="tb" style={HANDLE_STYLE} />
      <Handle type="target" position={Position.Left}   id="tl" style={HANDLE_STYLE} />
      <div style={{
        background: accent, color: "#fff",
        padding: "6px 10px",
        borderRadius: data.collapsed ? 6 : "6px 6px 0 0",
        fontWeight: 600, fontSize: 11, letterSpacing: "0.03em",
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
      }}>
        <span>{data.label}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ opacity: 0.75, fontWeight: 400 }}>({data.alias})</span>
          <i className={`ti ${data.collapsed ? "ti-chevron-down" : "ti-chevron-up"}`} style={{ fontSize: 10, opacity: 0.8 }} />
        </div>
      </div>
      {!data.collapsed && (
        <div style={{ padding: "6px 10px 8px" }}>
          <div style={{ color: "var(--fg-3)", fontSize: 10, marginBottom: 4 }}>
            {data.schema === "cte" ? "CTE" : data.schema}
          </div>
          {data.columns.map((col) => {
            const hit = match.cols.has(col.name);
            return (
              <div key={col.name} style={{
                display: "flex", justifyContent: "space-between", gap: 12,
                padding: "2px 4px", margin: "0 -4px",
                color: "var(--fg-2)", borderBottom: "1px solid var(--bg-3)",
                background: hit ? "rgba(220,106,76,0.18)" : "transparent",
                borderRadius: hit ? 3 : 0,
              }}>
                <span style={{ fontFamily: "var(--font-mono, monospace)", fontSize: 11, fontWeight: hit ? 600 : 400 }}>{col.name}</span>
                {col.note && <span style={{ color: "var(--fg-3)", fontSize: 10, whiteSpace: "nowrap" }}>{col.note}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const nodeTypes = { table: TableNodeComponent };
const edgeTypes = { floating: FloatingEdge };

// ─── Public component ────────────────────────────────────────────────────────

export function SchemaDiagram({
  tables,
  joins,
  height = 600,
}: {
  tables: TableSpec[];
  joins: JoinSpec[];
  height?: number;
}) {
  const baseNodes = useMemo<TableNode[]>(
    () => tables.map((t) => ({
      id: t.id,
      type: "table",
      position: { x: t.x, y: t.y },
      data: { label: t.label, schema: t.schema, alias: t.alias, columns: t.columns, collapsed: false },
    })),
    [tables],
  );
  const baseEdges = useMemo<Edge[]>(
    () => joins.map((j) => ({
      id: j.id,
      type: "floating",
      source: j.source,
      target: j.target,
      data: {
        label: j.label,
        edgeStyle: { stroke: KIND_COLOR[j.kind], strokeWidth: 1.5, strokeDasharray: KIND_DASH[j.kind] ?? "none" },
        labelColor: KIND_COLOR[j.kind],
      },
    })),
    [joins],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState<TableNode>(baseNodes);
  const [edges, , onEdgesChange] = useEdgesState<Edge>(baseEdges);
  const [search, setSearch] = useState("");
  const [allCollapsed, setAllCollapsedState] = useState(false);

  const handleNodeClick = useCallback(
    (_e: React.MouseEvent, node: TableNode) => {
      setNodes((prev) =>
        prev.map((n) => (n.id === node.id ? { ...n, data: { ...n.data, collapsed: !n.data.collapsed } } : n)),
      );
    },
    [setNodes],
  );

  const setAllCollapsed = useCallback((collapsed: boolean) => {
    setNodes((nds) => nds.map((n) => ({ ...n, data: { ...n.data, collapsed } })));
    setAllCollapsedState(collapsed);
  }, [setNodes]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 280 }}>
          <i
            className="ti ti-search"
            style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "var(--fg-3)", pointerEvents: "none" }}
          />
          <input
            type="text"
            placeholder="Search tables or columns"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input"
            style={{ padding: "6px 28px 6px 28px", width: "100%", fontSize: 12 }}
          />
          {search && (
            <button
              type="button" onClick={() => setSearch("")} aria-label="Clear search"
              style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", cursor: "pointer", color: "var(--fg-3)", padding: 4, display: "flex", alignItems: "center" }}
            >
              <i className="ti ti-x" style={{ fontSize: 12 }} />
            </button>
          )}
        </div>
        <span style={{ fontSize: 11, color: "var(--fg-3)" }}>Click a table to collapse / expand. Drag to rearrange.</span>
      </div>
      <div style={{ height, border: "1px solid var(--border-default)", borderRadius: 8, overflow: "hidden" }}>
        <SearchContext.Provider value={search}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            connectionMode={ConnectionMode.Loose}
            fitView
            fitViewOptions={{ padding: 0.12 }}
            minZoom={0.2}
            maxZoom={2}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="var(--border-default)" gap={20} size={1} />
            <ResetButton baseTables={tables} setNodes={setNodes} allCollapsed={allCollapsed} setAllCollapsed={setAllCollapsed} />
            <MiniMap
              nodeColor={(n) => SCHEMA_COLORS[(n as TableNode).data?.schema] ?? "#ccc"}
              style={{ background: "var(--bg-2)" }}
            />
          </ReactFlow>
        </SearchContext.Provider>
      </div>
      <Legend />
    </div>
  );
}

function ResetButton({
  baseTables, setNodes, allCollapsed, setAllCollapsed,
}: {
  baseTables: TableSpec[];
  setNodes: (updater: (prev: TableNode[]) => TableNode[]) => void;
  allCollapsed: boolean;
  setAllCollapsed: (collapsed: boolean) => void;
}) {
  const { fitView } = useReactFlow();
  const handleReset = useCallback(() => {
    setNodes(() => baseTables.map((t) => ({
      id: t.id,
      type: "table",
      position: { x: t.x, y: t.y },
      data: { label: t.label, schema: t.schema, alias: t.alias, columns: t.columns, collapsed: false },
    })));
    setTimeout(() => fitView({ padding: 0.12, duration: 400 }), 50);
  }, [baseTables, setNodes, fitView]);
  return (
    <Controls showFitView={false}>
      <ControlButton onClick={handleReset} title="Reset layout">
        <i className="ti ti-refresh" style={{ fontSize: 14 }} />
      </ControlButton>
      <ControlButton onClick={() => setAllCollapsed(!allCollapsed)} title={allCollapsed ? "Expand all tables" : "Collapse all tables"}>
        <i className={`ti ${allCollapsed ? "ti-layout-rows" : "ti-layout-navbar"}`} style={{ fontSize: 14 }} />
      </ControlButton>
    </Controls>
  );
}

function Legend() {
  return (
    <div style={{ display: "flex", gap: 16, fontSize: 11, color: "var(--fg-3)", flexWrap: "wrap" }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 18, height: 0, borderTop: `2px solid ${KIND_COLOR.direct}` }} />
        direct join
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 18, height: 0, borderTop: `2px dashed ${KIND_COLOR.cte}` }} />
        aggregating CTE
      </span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 18, height: 0, borderTop: `2px dashed ${KIND_COLOR.email}` }} />
        email / loose join
      </span>
    </div>
  );
}
